import { createSlice } from "@reduxjs/toolkit";
import {
  fetchClientsData,
  fetchClientById,
  fetchSaasModules,
  fetchClientSaasModules,
  fetchClientAttachments,
} from "../Thunks/clientsThunk";
import { addAsyncResourceThunk, isSupersededRequestPayload } from "../helpers/asyncResource";

const emptyDetail = () => ({
  loading: false,
  error: null,
  raw: null,
  requestId: null,
});

const emptySaasModules = () => ({
  loading: false,
  error: null,
  raw: null,
});

const emptyClientSaasModules = () => ({
  loading: false,
  error: null,
  raw: null,
  clientId: null,
});

const emptyAttachments = () => ({
  loading: false,
  error: null,
  raw: null,
  clientId: null,
});

const initialState = {
  data: null,
  loading: false,
  error: null,
  allListLoaded: false,
  allListData: null,
  clientDetail: emptyDetail(),
  saasModules: emptySaasModules(),
  clientSaasModules: emptyClientSaasModules(),
  attachments: emptyAttachments(),
};

export const clientsSlice = createSlice({
  name: "clients",
  initialState,
  reducers: {
    clearClients: (state) => {
      state.data = null;
      state.error = null;
      state.allListLoaded = false;
      state.allListData = null;
      state.clientDetail = emptyDetail();
      state.saasModules = emptySaasModules();
      state.clientSaasModules = emptyClientSaasModules();
      state.attachments = emptyAttachments();
    },
  },
  extraReducers: (builder) => {
    addAsyncResourceThunk(builder, fetchClientsData, {
      onFulfilled: (state, action) => {
        state.data = action.payload;
        if (action.meta?.arg?.getAll === true) {
          state.allListLoaded = true;
          state.allListData = action.payload;
        }
      },
    });
    builder
      .addCase(fetchClientById.pending, (state, action) => {
        state.clientDetail.loading = true;
        state.clientDetail.error = null;
        state.clientDetail.raw = null;
        state.clientDetail.requestId = action.meta.arg;
        state.clientSaasModules = emptyClientSaasModules();
        state.attachments = emptyAttachments();
      })
      .addCase(fetchClientById.fulfilled, (state, action) => {
        state.clientDetail.loading = false;
        state.clientDetail.error = null;
        state.clientDetail.raw = action.payload;
        state.clientDetail.requestId = action.meta.arg;
      })
      .addCase(fetchClientById.rejected, (state, action) => {
        if (isSupersededRequestPayload(action.payload)) return;
        state.clientDetail.loading = false;
        state.clientDetail.error =
          action.payload ?? action.error?.message ?? "Failed to fetch client data";
        state.clientDetail.raw = null;
      })
      .addCase(fetchSaasModules.pending, (state) => {
        state.saasModules.loading = true;
        state.saasModules.error = null;
      })
      .addCase(fetchSaasModules.fulfilled, (state, action) => {
        state.saasModules.loading = false;
        state.saasModules.error = null;
        state.saasModules.raw = action.payload;
      })
      .addCase(fetchSaasModules.rejected, (state, action) => {
        if (isSupersededRequestPayload(action.payload)) return;
        state.saasModules.loading = false;
        state.saasModules.error =
          action.payload ?? action.error?.message ?? "Failed to fetch SaaS modules";
      })
      .addCase(fetchClientSaasModules.pending, (state, action) => {
        state.clientSaasModules.loading = true;
        state.clientSaasModules.error = null;
        state.clientSaasModules.raw = null;
        state.clientSaasModules.clientId = action.meta.arg;
      })
      .addCase(fetchClientSaasModules.fulfilled, (state, action) => {
        state.clientSaasModules.loading = false;
        state.clientSaasModules.error = null;
        state.clientSaasModules.raw = action.payload;
        state.clientSaasModules.clientId = action.meta.arg;
      })
      .addCase(fetchClientSaasModules.rejected, (state, action) => {
        if (isSupersededRequestPayload(action.payload)) return;
        state.clientSaasModules.loading = false;
        state.clientSaasModules.error =
          action.payload ?? action.error?.message ?? "Failed to fetch client SaaS modules";
        state.clientSaasModules.raw = null;
      })
      .addCase(fetchClientAttachments.pending, (state, action) => {
        state.attachments.loading = true;
        state.attachments.error = null;
        state.attachments.raw = null;
        state.attachments.clientId = action.meta.arg;
      })
      .addCase(fetchClientAttachments.fulfilled, (state, action) => {
        state.attachments.loading = false;
        state.attachments.error = null;
        state.attachments.raw = action.payload;
        state.attachments.clientId = action.meta.arg;
      })
      .addCase(fetchClientAttachments.rejected, (state, action) => {
        if (isSupersededRequestPayload(action.payload)) return;
        state.attachments.loading = false;
        state.attachments.error =
          action.payload ?? action.error?.message ?? "Failed to fetch attachments";
        state.attachments.raw = null;
      });
  },
});

export const { clearClients } = clientsSlice.actions;
export const selectClientsData = (state) => state.clients.data;
export const selectClientsLoading = (state) => state.clients.loading;
export const selectClientsError = (state) => state.clients.error;
export const selectClientDetail = (state) => state.clients.clientDetail;
export const selectClientDetailLoading = (state) => state.clients.clientDetail.loading;
export const selectClientDetailError = (state) => state.clients.clientDetail.error;
export const selectClientDetailRaw = (state) => state.clients.clientDetail.raw;
export const selectSaasModulesState = (state) => state.clients.saasModules;
export const selectClientSaasModulesState = (state) => state.clients.clientSaasModules;
export const selectClientAttachmentsState = (state) => state.clients.attachments;

export default clientsSlice.reducer;
